// /frontend/src/components/LinkedInReminderToast.jsx
import React, { useState } from "react";
import { toast } from "react-toastify";
import API from "../helpers/API";

const LinkedInReminderToastContent = ({
  userId,
  currentUrl,
  onSaveSuccess,
  closeToast
}) => {
  const [linkedinUrl, setLinkedinUrl] = useState(currentUrl || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const validateUrl = (url) => {
    const pattern = /^https?:\/\/(www\.)?linkedin\.com\/in\/.+/i;
    return pattern.test(url.trim());
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = linkedinUrl.trim();

    if (!trimmed) {
      setError("LinkedIn URL is required.");
      return;
    }
    if (!validateUrl(trimmed)) {
      setError(
        "Please enter a valid LinkedIn URL (e.g., https://www.linkedin.com/in/yourname)"
      );
      return;
    }

    setError("");
    setSaving(true);
    try {
      const res = await API.patch("/users/update-linkedin", {
        linkedin_url: trimmed
      });
      if (res.data.success) {
        if (onSaveSuccess) {
          onSaveSuccess(trimmed, res.data);
        }
        try {
          sessionStorage.removeItem(`linkedinReminderShown:${userId}`);
        } catch (e) {}
        closeToast();
      } else {
        setError(res.data.message || "Failed to update LinkedIn URL.");
      }
    } catch (err) {
      console.error("LinkedIn update error:", err);
      setError(
        err.response?.data?.message || "Network error. Please try again later."
      );
    } finally {
      setSaving(false);
    }
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000
      }}
      onClick={closeToast}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          maxWidth: "500px",
          width: "90%",
          padding: "28px 24px",
          boxShadow: "0 20px 35px -8px rgba(0, 0, 0, 0.2)",
          animation: "slideIn 0.3s ease-out"
        }}
        onClick={stopPropagation}
      >
        <style>
          {`
            @keyframes slideIn {
              from { opacity: 0; transform: translateY(-30px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}
        </style>
        <div style={{ textAlign: "center", marginBottom: "16px" }}>
          <span style={{ fontSize: "48px" }}>🔗</span>
        </div>
        <h3
          style={{
            fontSize: "1.6rem",
            fontWeight: 600,
            marginBottom: "12px",
            color: "#001524",
            textAlign: "center"
          }}
        >
          Complete Your Profile
        </h3>
        <p
          style={{
            color: "#4b5563",
            lineHeight: 1.5,
            marginBottom: "20px",
            fontSize: "0.95rem",
            textAlign: "center"
          }}
        >
          Adding your LinkedIn profile URL is required before applying for jobs.
          It helps employers verify your professional background and increases
          your chances of being selected.
        </p>
        <form onSubmit={handleSave}>
          <div style={{ marginBottom: "16px" }}>
            <label
              htmlFor="linkedin-reminder-input"
              style={{
                display: "block",
                marginBottom: "6px",
                fontWeight: 500,
                color: "#1e293b"
              }}
            >
              LinkedIn Profile URL <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              id="linkedin-reminder-input"
              type="url"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "1rem",
                outline: "none",
                transition: "border-color 0.2s"
              }}
              placeholder="https://www.linkedin.com/in/yourname"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              disabled={saving}
              autoFocus
            />
            {error && (
              <p
                style={{
                  color: "#dc2626",
                  fontSize: "0.85rem",
                  marginTop: "4px",
                  marginBottom: 0
                }}
              >
                {error}
              </p>
            )}
            <p
              style={{
                color: "#6b7280",
                fontSize: "0.8rem",
                marginTop: "4px",
                marginBottom: 0
              }}
            >
              Format: https://www.linkedin.com/in/your-name
            </p>
          </div>
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "24px"
            }}
          >
            <button
              type="button"
              onClick={closeToast}
              style={{
                padding: "10px 20px",
                borderRadius: "30px",
                border: "none",
                background: "#e5e7eb",
                color: "#374151",
                fontWeight: 500,
                cursor: "pointer",
                transition: "background 0.2s"
              }}
              disabled={saving}
            >
              Remind Me Later
            </button>
            <button
              type="submit"
              style={{
                padding: "10px 20px",
                borderRadius: "30px",
                border: "none",
                background: "#0077b5",
                color: "white",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "background 0.2s, transform 0.1s"
              }}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                    style={{
                      width: "1rem",
                      height: "1rem",
                      borderWidth: "2px"
                    }}
                  />
                  Saving...
                </>
              ) : (
                "Save LinkedIn URL"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const showLinkedInReminderToast = (user, setAuth) => {
  if (!user) return;

  const userRole = user.role ?? user.Role;
  if (userRole !== 2) return;

  const linkedin =
    user.linkedin_url ||
    user.linkedInUrl ||
    user.LinkedInUrl ||
    user.LinkedInURL ||
    "";
  if (linkedin && linkedin.trim() !== "") return;

  const userId = user.userId || user._id || user.id;
  if (!userId) {
    console.warn("Cannot show LinkedIn reminder: missing user ID");
    return;
  }

  const storageKey = `linkedinReminderShown:${userId}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, "true");
  } catch (e) {}

  const toastId = toast(
    ({ closeToast }) => (
      <LinkedInReminderToastContent
        userId={userId}
        currentUrl={linkedin}
        onSaveSuccess={(newUrl, responseData) => {
          if (responseData.token && responseData.user) {
            setAuth({
              user: responseData.user,
              token: responseData.token
            });
            localStorage.setItem(
              "userAuthDetails",
              JSON.stringify({
                user: responseData.user,
                token: responseData.token
              })
            );
          } else {
            setAuth((prev) => ({
              ...prev,
              user: {
                ...prev.user,
                linkedin_url: newUrl
              }
            }));
            const storedAuth = JSON.parse(
              localStorage.getItem("userAuthDetails")
            );
            if (storedAuth) {
              localStorage.setItem(
                "userAuthDetails",
                JSON.stringify({
                  ...storedAuth,
                  user: {
                    ...storedAuth.user,
                    linkedin_url: newUrl
                  }
                })
              );
            }
          }
          try {
            sessionStorage.removeItem(storageKey);
          } catch (e) {}
        }}
        closeToast={closeToast}
      />
    ),
    {
      closeButton: false,
      closeOnClick: false,
      draggable: false,
      autoClose: false,
      position: "top-center",
      hideProgressBar: true,
      style: {
        background: "transparent",
        boxShadow: "none",
        padding: 0,
        width: "auto",
        maxWidth: "none"
      }
    }
  );

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      toast.dismiss(toastId);
      document.removeEventListener("keydown", handleKeyDown);
    }
  };
  document.addEventListener("keydown", handleKeyDown);
  const unsubscribe = toast.onChange((payload) => {
    if (payload.id === toastId && payload.status === "removed") {
      document.removeEventListener("keydown", handleKeyDown);
      unsubscribe();
    }
  });
};

export default LinkedInReminderToastContent;
